"use client";

import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useBillingState } from "@/hooks/useBillingState";
import { getClientPlan } from "@/lib/plan";
import { usePaywall } from "@/components/pricing/PaywallProvider";

import { Minus, Plus } from "lucide-react";

interface PracticeOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartPractice: (options: { numQuestions: number; skipCompleted: boolean; wrongOnly: boolean; mode: 'instant' | 'mock' }) => void;
  totalQuestions: number;
}

const AVERAGE_TIME_PER_QUESTION = 225; // 3.75 minutes in seconds

export default function PracticeOptionsModal({ isOpen, onClose, onStartPractice, totalQuestions }: PracticeOptionsModalProps) {
  const [modalStep, setModalStep] = React.useState('options');
  const [numQuestions, setNumQuestions] = React.useState(10);
  const [skipCompleted, setSkipCompleted] = React.useState(false);
  const [wrongOnly, setWrongOnly] = React.useState(false);

  const { billing } = useBillingState();
  const { openPaywall } = usePaywall();
  const plan = getClientPlan(billing);
  // Free users can't pick more than today's remaining allowance: the server
  // rejects the whole session (premium_required) rather than trimming it.
  const freeCap = billing && !plan.premium ? plan.questionBalance : null;
  const poolCap = Math.min(totalQuestions, 30);
  const maxAllowedQuestions = freeCap === null ? poolCap : Math.min(poolCap, freeCap);

  React.useEffect(() => {
    if (isOpen) {
      setModalStep('options'); // Reset to first step when modal opens
    }
  }, [isOpen]);

  React.useEffect(() => {
    // Adjust numQuestions if it exceeds the new max allowed questions, or if it's zero
    const initialQuestions = Math.min(numQuestions, maxAllowedQuestions);
    setNumQuestions(initialQuestions > 0 ? initialQuestions : 1);
  }, [maxAllowedQuestions]);

  const estimatedTime = React.useMemo(() => {
    const timeInSeconds = numQuestions * AVERAGE_TIME_PER_QUESTION;
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }, [numQuestions]);

  const handleNext = () => {
    setModalStep('mode');
  };

  const handleStart = (mode: 'instant' | 'mock') => {
    onStartPractice({ numQuestions, skipCompleted, wrongOnly, mode });
  };

  const hitFreeCap = (amount: number) => freeCap !== null && amount > freeCap && amount <= poolCap;

  const showUpgrade = () => {
    openPaywall({ defaultTab: 'subscription', trigger: 'practice_options_limit', context: { remaining: freeCap } });
  };

  const handlePreset = (amount: number) => {
    if (hitFreeCap(amount)) {
      showUpgrade();
      return;
    }
    setNumQuestions(Math.min(amount, maxAllowedQuestions));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        {modalStep === 'options' && (
          <>
            <DialogHeader>
              <DialogTitle>Practice Options</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="num-questions">Number of Questions</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setNumQuestions(Math.max(1, numQuestions - 1))} disabled={numQuestions <= 1}>
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    id="num-questions"
                    type="number"
                    value={numQuestions}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (isNaN(val)) {
                        setNumQuestions(1);
                      } else {
                        setNumQuestions(Math.max(1, Math.min(maxAllowedQuestions, val)));
                      }
                    }}
                    min={1}
                    max={maxAllowedQuestions}
                    className="text-center"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => (hitFreeCap(numQuestions + 1) ? showUpgrade() : setNumQuestions(Math.min(maxAllowedQuestions, numQuestions + 1)))}
                    disabled={numQuestions >= poolCap}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[5, 10, 15, 20].map((preset) => (
                    <Button 
                      key={preset}
                      variant="outline" 
                      size="sm" 
                      onClick={() => handlePreset(preset)}
                      disabled={preset > poolCap}
                    >
                      {preset}
                    </Button>
                  ))}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setNumQuestions(maxAllowedQuestions)}
                    disabled={maxAllowedQuestions === 0}
                  >
                    Max ({maxAllowedQuestions})
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Estimated time: {estimatedTime}
                </p>
                {freeCap !== null && (
                  <p className="text-sm text-muted-foreground">
                    {freeCap === 0 ? "No free questions left today." : `${freeCap} free question${freeCap === 1 ? "" : "s"} left today.`}{" "}
                    <button type="button" onClick={showUpgrade} className="text-primary underline underline-offset-4 hover:opacity-80">
                      Upgrade for unlimited
                    </button>
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="skip-completed" checked={skipCompleted} onCheckedChange={(checked) => {
                  setSkipCompleted(checked as boolean);
                  if (checked) {
                    setWrongOnly(false);
                  }
                }} />
                <Label htmlFor="skip-completed">Skip completed questions</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="wrong-only" checked={wrongOnly} onCheckedChange={(checked) => {
                  setWrongOnly(checked as boolean);
                  if (checked) {
                    setSkipCompleted(false);
                  }
                }} />
                <Label htmlFor="wrong-only">Cover wrong questions only</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleNext} disabled={numQuestions > maxAllowedQuestions}>Next</Button>
            </DialogFooter>
          </>
        )}
        {modalStep === 'mode' && (
          <>
            <DialogHeader>
              <DialogTitle>Choose Your Mode</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 py-4">
              <div 
                className="p-6 border rounded-lg hover:bg-muted/50 cursor-pointer flex flex-col items-center text-center"
                onClick={() => handleStart('instant')}
              >
                <h3 className="text-lg font-semibold">Instant Feedback</h3>
                <p className="text-sm text-muted-foreground mt-2">Get instant answers and feedback from Turing AI after each question.</p>
              </div>
              <div 
                className="p-6 border rounded-lg hover:bg-muted/50 cursor-pointer flex flex-col items-center text-center"
                onClick={() => handleStart('mock')}
              >
                <h3 className="text-lg font-semibold">Mock Exam Mode</h3>
                <p className="text-sm text-muted-foreground mt-2">Simulate a real exam. Answers and results will be revealed at the end.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalStep('options')}>Back</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
